import { useState, useEffect, useCallback } from "react";
import { api } from "@/services/api";

interface Tx {
  id: number;
  type: string;
  amount: string;
  currency: string;
  method: string;
  status: string;
  reference: string;
  transaction_date: string;
  lead_name: string;
  property_title: string;
  recorded_by: string;
}

interface ListResponse { data: Tx[]; total: number; page: number; limit: number }

const STATUS_BADGE: Record<string, string> = {
  completed: "bg-primary-600 text-background-50",
  pending: "bg-accent-100 text-accent-900",
  failed: "bg-background-200 text-foreground-700",
  refunded: "bg-secondary-100 text-secondary-900",
};

export default function AdminTransactions() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (type) params.set("type", type);
    params.set("page", String(page));
    params.set("limit", "15");
    try {
      const res = await api.get<ListResponse>(`/transactions?${params.toString()}`);
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [status, type, page]);

  useEffect(() => { load(); }, [load]);

  function toggleSelect(id: number) {
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  }

  async function applyBulk() {
    if (!bulkStatus || selected.length === 0) return;
    try {
      await api.patch("/transactions/bulk-status", { ids: selected, status: bulkStatus });
      setSelected([]);
      setBulkStatus("");
      load();
    } catch (e: any) {
      alert(e?.message || "Bulk update failed");
    }
  }

  const pageCount = data ? Math.max(Math.ceil(data.total / data.limit), 1) : 1;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400">
            <option value="">All Status</option>
            {["completed", "pending", "failed", "refunded"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400">
            <option value="">All Types</option>
            {["sale", "booking", "token", "installment", "commission"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700">
          <i className="ri-add-line"></i> Record Transaction
        </button>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-primary-50 border border-primary-200 p-3">
          <span className="text-sm text-foreground-800">{selected.length} selected</span>
          <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} className="rounded-lg border border-primary-200 bg-background-50 px-3 py-1.5 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400">
            <option value="">Bulk set status...</option>
            {["completed", "pending", "failed", "refunded"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={applyBulk} disabled={!bulkStatus} className="inline-flex items-center gap-1 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-4 py-2 hover:bg-primary-700 disabled:opacity-50">
            Apply
          </button>
          <button onClick={() => setSelected([])} className="text-sm text-foreground-600 hover:text-foreground-800">Clear</button>
        </div>
      )}

      {error && <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">{error}</div>}

      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left text-xs uppercase tracking-wider text-foreground-600">
                <th className="px-4 py-3 w-10"><input type="checkbox" checked={data ? selected.length === data.data.length && data.data.length > 0 : false} onChange={(e) => { if (!data) return; setSelected(e.target.checked ? data.data.map((t) => t.id) : []); }} /></th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Reference</th>
                <th className="px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {loading && (<tr><td colSpan={7} className="px-4 py-10 text-center text-foreground-600"><i className="ri-loader-4-line animate-spin"></i> Loading...</td></tr>)}
              {!loading && data?.data.map((t) => (
                <tr key={t.id} className="hover:bg-background-100/60">
                  <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggleSelect(t.id)} /></td>
                  <td className="px-4 py-3 capitalize text-foreground-800">{t.type}</td>
                  <td className="px-4 py-3 font-semibold text-foreground-950">{t.currency} {Number(t.amount || 0).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${STATUS_BADGE[t.status] || "bg-background-200 text-foreground-700"}`}>{t.status}</span></td>
                  <td className="px-4 py-3 capitalize text-foreground-700">{t.method || "—"}</td>
                  <td className="px-4 py-3 text-foreground-700">{t.reference || "—"}</td>
                  <td className="px-4 py-3 text-foreground-700">{t.transaction_date}</td>
                </tr>
              ))}
              {!loading && data?.data.length === 0 && (<tr><td colSpan={7} className="px-4 py-12 text-center text-foreground-600">No transactions found.</td></tr>)}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-background-100">
          <p className="text-xs text-foreground-600">Total: {data?.total ?? 0}</p>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page <= 1} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40"><i className="ri-arrow-left-s-line"></i></button>
            <span className="px-3 text-sm text-foreground-700">{page} / {pageCount}</span>
            <button onClick={() => setPage((p) => Math.min(p + 1, pageCount))} disabled={page >= pageCount} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40"><i className="ri-arrow-right-s-line"></i></button>
          </div>
        </div>
      </div>

      {createOpen && <CreateTx onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />}
    </div>
  );
}

function CreateTx({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ type: "sale", amount: "", currency: "INR", method: "", reference: "", description: "", transaction_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/transactions", form);
      onCreated();
    } catch (err: any) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-3xl bg-background-50 border border-background-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100">
          <h3 className="font-heading text-lg text-foreground-950">Record Transaction</h3>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Close"><i className="ri-close-line"></i></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Type *</label>
              <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
                {["sale", "booking", "token", "installment", "commission"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Amount *</label>
              <input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Reference</label>
            <input value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Date</label>
            <input type="date" value={form.transaction_date} onChange={(e) => setForm((f) => ({ ...f, transaction_date: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-5 py-2.5 hover:bg-background-100">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700 disabled:opacity-60">
              {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}